import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  Signal
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ButtonComponent, MultiselectItem } from '@ui/aos-lib';

import { FilterFieldMapping, ReportGroupsFilter, ReportGroupsFilterForm } from './report-groups-filter-criteria.types';
import { ReportGroupsDynamicFilterComponent } from './report-groups-dynamic-filter/report-groups-dynamic-filter.component';
import { UdfApiResponse } from '../../report-groups.types';
import { isSingleCommodityClient } from '../../../../help-and-settings/user-settings/store/user-settings.reducer';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { udfAttributeType, udfSubcategory } from '../../report-groups.constants';

@Component({
  selector: 'aos-report-groups-filter-criteria',
  imports: [ReactiveFormsModule, ButtonComponent, ReportGroupsDynamicFilterComponent, TranslateModule],
  styleUrl: './report-groups-filter-criteria.component.scss',
  templateUrl: './report-groups-filter-criteria.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGroupsFilterCriteriaComponent {
  @Input() set setUdfToFiltersDropDownItem(udf: UdfApiResponse[] | undefined) {
    if (udf?.length) {
      const insertPositionBeforeLasso = this.initialFilters().items.length - 1;
      const preparedUdf = udf.map(({ name }) => ({
        text: name,
        value: name,
        subcategory: udfSubcategory,
        attributeType: udfAttributeType,
        fieldName: name
      }));
      this.initialFilters().items.splice(insertPositionBeforeLasso, 0, ...preparedUdf);
    }
  }
  form = input.required<FormArray>();

  private readonly formChange = effect(() => {
    const form = this.form();
    if (!form) {
      return;
    }

    if (this.formValueChangeSubscription) {
      this.formValueChangeSubscription.unsubscribe();
      this.formValueChangeSubscription = null;
    }

    this.formValueChangeSubscription = form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formGroups: ReportGroupsFilterForm[]) => {
        this.updateMultiselectItemIsDisabled(
          formGroups.map(formGroup => formGroup.name).filter(formGroupItem => formGroupItem)
        );
        this.cdRef.markForCheck();
      });
  });
  private formValueChangeSubscription: Subscription | null = null;

  protected filterCriteriaFormArrayControls: Signal<FormControl<MultiselectItem>[]> = computed(() => {
    return this.form()?.controls as FormControl<MultiselectItem>[];
  });

  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  protected readonly filterByLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.TITLE.FILTER_BY;
  protected readonly allConditionsAppliedLabel =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM
      .ALL_CONDITIONS_APPLIED_TO_A_REPORT_GROUP_WILL_BE_JOINED_USING_AN;
  protected readonly operatirMeaningLabel =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM
      .OPERATOR_MEANING_ALL_CRITERIA_MUST_BE_MET_FOR_A_DEVICE_TO_BE_INCLUDED;
  protected readonly addLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.ADD;

  protected readonly defaultFilter = { text: TranslateRegistry.get.FILTER.COMMON.SELECT, value: null };
  protected initialFilters: Signal<ReportGroupsFilter> = computed(() => {
    const isSingleCommodityUser = this.#isSingleCommodityClient();
    const items = this.translate.getTranslatedItems(this.getFilterStaticItems());
    const defaultFilterTranslated = this.translate.getTranslatedItems([this.defaultFilter])[0];

    return {
      selected: [defaultFilterTranslated],
      items: [
        defaultFilterTranslated,
        ...(isSingleCommodityUser ? items.filter(item => item.value !== FilterFieldMapping.Commodity.value) : items)
      ]
    };
  });

  protected get isAllFiltersSelected(): boolean {
    const filterByValues: MultiselectItem[] = this.filterCriteriaFormArrayControls()?.map(ctrl => ctrl.value);
    const initialFiltersItems = this.initialFilters().items.filter(item => item.value !== this.defaultFilter.value);
    return (
      initialFiltersItems.every(item => filterByValues?.includes(item)) ||
      filterByValues?.length === initialFiltersItems.length
    );
  }

  protected addFilter(): void {
    const form = this.form();
    if (this.isAllFiltersSelected || !form) {
      return;
    }

    form.push(
      this.fb.group<ReportGroupsFilterForm>({
        name: this.initialFilters().selected[0]
      })
    );
  }

  protected removeFilterAtIndex(index: number): void {
    const form = this.form();
    if (!form) {
      return;
    }

    form.removeAt(index);
    form.markAsDirty();
    form.markAsTouched();
  }

  private updateMultiselectItemIsDisabled(selectedMultiselectItems: MultiselectItem[]): void {
    this.initialFilters().items.forEach(filterItem => {
      const isItemSelected = !!selectedMultiselectItems.find(
        selectedFilterItem => selectedFilterItem.value === filterItem.value
      );
      filterItem.isDisabled = isItemSelected;
    });
  }

  private getFilterStaticItems(): MultiselectItem[] {
    return Object.keys(FilterFieldMapping).map(key => ({
      text: FilterFieldMapping[key]?.text,
      value: FilterFieldMapping[key]?.value,
      subcategory: FilterFieldMapping[key]?.subcategory,
      fieldName: FilterFieldMapping[key]?.fieldName,
      attributeType: FilterFieldMapping[key]?.attributeType
    }));
  }
}
